import React, { useEffect, useState } from "react";

export const ShootingStars = ({
  minSpeed = 10,
  maxSpeed = 30,
  minDelay = 1200,
  maxDelay = 4200,
  starColor = "#9E00FF",
  trailColor = "#2EB9DF",
  starWidth = 10,
  starHeight = 1,
  className = "",
}) => {
  const [stars, setStars] = useState([]);

  useEffect(() => {
    const createStar = () => {
      const star = {
        id: Date.now(),
        x: Math.random() * window.innerWidth,
        y: -10,
        angle: Math.random() * 60 - 30, // -30 to 30 degrees
        speed: Math.random() * (maxSpeed - minSpeed) + minSpeed,
        length: Math.random() * 80 + 20, // 20 to 100
        delay: Math.random() * (maxDelay - minDelay) + minDelay,
      };
      return star;
    };

    const generateStars = () => {
      const newStar = createStar();
      setStars((prevStars) => [...prevStars, newStar]);

      // Remove star after animation
      setTimeout(() => {
        setStars((prevStars) => prevStars.filter((s) => s.id !== newStar.id));
      }, 5000);
    };

    // Generate stars at intervals
    const interval = setInterval(generateStars, 800);

    return () => clearInterval(interval);
  }, [minSpeed, maxSpeed, minDelay, maxDelay]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute"
          style={{
            left: `${star.x}px`,
            top: `${star.y}px`,
            transform: `rotate(${star.angle}deg)`,
            animation: `shoot ${star.speed}s linear`,
            animationDelay: `${star.delay}ms`,
          }}
        >
          {/* Star head */}
          <div
            className="absolute rounded-full"
            style={{
              width: `${starWidth}px`,
              height: `${starHeight}px`,
              background: starColor,
              boxShadow: `0 0 6px 2px ${starColor}`,
            }}
          />
          {/* Star trail */}
          <div
            className="absolute"
            style={{
              width: `${star.length}px`,
              height: `${starHeight}px`,
              background: `linear-gradient(to right, ${trailColor}, transparent)`,
              transform: 'translateX(-100%)',
              opacity: 0.5,
            }}
          />
        </div>
      ))}
      <style jsx>{`
        @keyframes shoot {
          0% {
            transform: translateY(0) translateX(0) rotate(45deg);
            opacity: 1;
          }
          70% {
            opacity: 1;
          }
          100% {
            transform: translateY(300px) translateX(300px) rotate(45deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export const StarsBackground = ({ 
  starDensity = 0.00015,
  allStarsTwinkle = true,
  twinkleProbability = 0.7,
  minTwinkleSpeed = 0.5,
  maxTwinkleSpeed = 1,
  className = "",
}) => {
  const [stars, setStars] = useState([]);

  useEffect(() => {
    const generateStars = () => {
      const isMobile = window.innerWidth < 768;
      const actualDensity = isMobile ? starDensity * 0.2 : starDensity; // Reduce density heavily on mobile
      const starCount = Math.floor(window.innerWidth * window.innerHeight * actualDensity);
      
      const newStars = Array.from({ length: starCount }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.5 + 0.5,
        twinkle: allStarsTwinkle || Math.random() < twinkleProbability,
        twinkleSpeed: Math.random() * (maxTwinkleSpeed - minTwinkleSpeed) + minTwinkleSpeed,
      }));
      setStars(newStars);
    };

    generateStars();
    
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(generateStars, 300); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, [starDensity, allStarsTwinkle, twinkleProbability, minTwinkleSpeed, maxTwinkleSpeed]);

  return (
    <div className={`absolute inset-0 ${className}`}>
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animation: star.twinkle ? `twinkle ${star.twinkleSpeed}s infinite alternate` : 'none',
          }}
        />
      ))}
      <style jsx>{`
        @keyframes twinkle {
          0% {
            opacity: 0.3;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};
